/**
 * Supabase client singleton.
 *
 * One client per browser tab (NOT per render): supabase-js holds the
 * session in localStorage + an in-memory cache, and the `onAuthStateChange`
 * listener fires *all* subscribers — so creating multiple clients
 * doubles up listeners and causes spurious refreshes.
 *
 * URL + anon key come from Vite env (see `frontend/.env.example`):
 *   VITE_SUPABASE_URL
 *   VITE_SUPABASE_ANON_KEY
 *
 * `persistSession=true` keeps the user logged in across refreshes; the
 * SDK takes care of token refresh in the background.
 */
import { createClient, SupabaseClient } from "@supabase/supabase-js";

const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

if (!url || !anonKey) {
  // We surface this in the UI rather than crashing the bundle: if the
  // ops setup is incomplete, the auth pages will show a friendly error
  // instead of a blank screen. (See AuthProvider.)
  // eslint-disable-next-line no-console
  console.warn(
    "Supabase env missing — VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY not set. " +
      "Auth UI will fall back to a configuration-error screen."
  );
}

export const supabase: SupabaseClient = createClient(
  url ?? "https://invalid.supabase.co",
  anonKey ?? "invalid-key",
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true, // needed for password-reset / magic-link callbacks
    },
  }
);

export const supabaseConfigured = Boolean(url && anonKey);
